/**
* A 3-dimensional vector. Immutable.
*  */
export class Vector {
    readonly x: number;
    readonly y: number;
    readonly z: number;

    constructor(x: number, y: number, z: number) {
        this.x = x;
        this.y = y;
        this.z = z;
    }

    /** Create instance from azimuth and elevation in radians */
    static fromAzElev(az: number, elev: number) {
        let cosElev = Math.cos(elev);
        return new Vector(Math.cos(az) * cosElev, Math.sin(az) * cosElev, Math.sin(elev))
    }

    /** Create instance from array of numbers representing x, y, z components */
    static fromArray(array: number[]) {
        return new Vector(array[0], array[1], array[2])
    }

    /** Convert to array of numbers representing the 3 components */
    toArray(): number[] {
        return [this.x, this.y, this.z];
    }

    get magnitudeSquared(): number {
        return this.x * this.x + this.y * this.y + this.z * this.z;
    }

    /**
    * Gets the magnitude of this vector
    */
    get magnitude(): number {
        return Math.sqrt(this.magnitudeSquared);
    }

    get latitude(): number {
        return Math.asin(this.z / this.magnitude);
    }

    get longitude(): number {
        return Math.atan2(this.y, this.x);
    }

    dotProduct(other: Vector): number {
        return this.x * other.x + this.y * other.y + this.z * other.z;
    }

    vectorProduct(other: Vector): Vector {
        return new Vector(this.y * other.z - this.z * other.y,
                          this.z * other.x - this.x * other.z,
                          this.x * other.y - this.y * other.x);
    }

    scale(factor: number): Vector {
        return new Vector(this.x * factor, this.y * factor, this.z * factor);
    }

    normalize(): Vector {
        return this.scale(1 / this.magnitude);
    }

    angleBetween(other: Vector): number {
        return Math.acos(this.dotProduct(other) / (this.magnitude * other.magnitude));
    }

    add(other: Vector): Vector {
        return new Vector(this.x + other.x, this.y + other.y, this.z + other.z);
    }

    subtract(other: Vector): Vector {
        return new Vector(this.x - other.x, this.y - other.y, this.z - other.z);
    }

    rotateAboutXAxis(angle: number): Vector {
        let sine = Math.sin(angle);
        let cosine = Math.cos(angle);
    
        return new Vector(this.x, cosine * this.y - sine * this.z, sine * this.y + cosine * this.z);
    }

    rotateAboutZAxis(angle: number): Vector {
        let sine = Math.sin(angle);
        let cosine = Math.cos(angle);
    
        return new Vector(cosine * this.x - sine * this.y, sine * this.x + cosine * this.y, this.z);
    }

    getElement(elem: number): number {
        switch (elem)
        {
            case 0:
                return this.x;
            case 1:
                return this.y;
            case 2:
                return this.z;
        }
    }
}



// WEBPACK FOOTER //
// ./src/Vector.ts